import React, { useEffect, useMemo, useState } from "react";
import TextFieldRow from "../../../../components/TextFieldRow";
import TextField from "../../../../components/TextField";

type Props = {
  numeroOS: string;
  setNumeroOS: (v: string) => void;

  nomeEmbarcacao: string;
  setNomeEmbarcacao: (v: string) => void;

  responsavelLiberacao: string;
  setResponsavelLiberacao: (v: string) => void;

  responsavelColeta: string;
  setResponsavelColeta: (v: string) => void;

  nomeEmpresa: string;
  setNomeEmpresa: (v: string) => void;

  empresaClienteId: number | null;
  setEmpresaClienteId: (v: number | null) => void;

  coordenadoresResponsaveisIds: number[];
  setCoordenadoresResponsaveisIds: (v: number[]) => void;

  coordenadoresResponsaveisNomes: string[];
  setCoordenadoresResponsaveisNomes: (v: string[]) => void;
};

const COLABS_URL =
  "https://api.eva-googlemarine.com/departamentos/recursos-humanos/colaboradores-nome";
const COORDS_URL =
  "https://api.eva-googlemarine.com/departamentos/recursos-humanos/colaboradores-coordenacao";

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

type EmpresaOption = {
  id: number;
  nome_fantasia: string;
};

type CoordenadorOption = {
  id: number;
  nome: string;
  empresas_responsavel: EmpresaOption[];
};

const ServicoMovement: React.FC<Props> = ({
  numeroOS,
  setNumeroOS,
  nomeEmbarcacao,
  setNomeEmbarcacao,
  responsavelColeta,
  setResponsavelColeta,
  nomeEmpresa,
  setNomeEmpresa,
  empresaClienteId,
  setEmpresaClienteId,
  coordenadoresResponsaveisIds,
  setCoordenadoresResponsaveisIds,
  coordenadoresResponsaveisNomes,
  setCoordenadoresResponsaveisNomes,
}) => {
  const [colabOptions, setColabOptions] = useState<string[]>([]);
  const [loadingColabs, setLoadingColabs] = useState(false);
  const [erroColabs, setErroColabs] = useState<string | null>(null);

  const [coordOptions, setCoordOptions] = useState<CoordenadorOption[]>([]);
  const [loadingCoords, setLoadingCoords] = useState(false);
  const [erroCoords, setErroCoords] = useState<string | null>(null);

  const toUpper = (fn: (v: string) => void) => (v: string) =>
    fn((v || "").toUpperCase());

  useEffect(() => {
    let alive = true;
    const ctrlColabs = new AbortController();
    const ctrlCoords = new AbortController();

    (async () => {
      setLoadingColabs(true);
      setErroColabs(null);
      try {
        const res = await fetch(COLABS_URL, {
          method: "GET",
          headers: { Accept: "application/json" },
          signal: ctrlColabs.signal,
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();

        const list: string[] = Array.isArray(json?.data)
          ? (json.data as Array<{ nome_completo?: string }>).map((x) =>
              (x?.nome_completo ?? "").trim()
            )
          : [];

        const unique = Array.from(
          new Set(
            list
              .map((s) => (typeof s === "string" ? s.trim().toUpperCase() : ""))
              .filter(Boolean)
          )
        );

        if (alive) setColabOptions(unique);
      } catch (e: any) {
        if (e?.name !== "AbortError" && alive) {
          setErroColabs(String(e?.message || e));
        }
      } finally {
        if (alive) setLoadingColabs(false);
      }

      await sleep(300);
      if (!alive) return;

      setLoadingCoords(true);
      setErroCoords(null);
      try {
        const res = await fetch(COORDS_URL, {
          method: "GET",
          headers: { Accept: "application/json" },
          credentials: "include",
          signal: ctrlCoords.signal,
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();

        const list: CoordenadorOption[] = Array.isArray(json?.data)
          ? (json.data as Array<any>)
              .map((x) => ({
                id: Number(x?.id),
                nome: String(x?.nome || "")
                  .trim()
                  .toUpperCase(),
                empresas_responsavel: Array.isArray(x?.empresas_responsavel)
                  ? x.empresas_responsavel
                      .map((e: any) => ({
                        id: Number(e?.id),
                        nome_fantasia: String(e?.nome_fantasia || "")
                          .trim()
                          .toUpperCase(),
                      }))
                      .filter(
                        (e: EmpresaOption) =>
                          Number.isFinite(e.id) && e.id > 0 && e.nome_fantasia
                      )
                  : [],
              }))
              .filter(
                (x) => Number.isFinite(x.id) && x.id > 0 && x.nome.length > 0
              )
          : [];

        if (alive) setCoordOptions(list);
      } catch (e: any) {
        if (e?.name !== "AbortError" && alive) {
          setErroCoords(String(e?.message || e));
        }
      } finally {
        if (alive) setLoadingCoords(false);
      }
    })();

    return () => {
      alive = false;
      ctrlColabs.abort();
      ctrlCoords.abort();
    };
  }, []);

  const colabDropdown = useMemo(
    () => (colabOptions.length ? colabOptions : undefined),
    [colabOptions]
  );

  const empresaOptions = useMemo(() => {
    const map = new Map<number, EmpresaOption>();

    for (const coord of coordOptions) {
      for (const emp of coord.empresas_responsavel || []) {
        if (!map.has(emp.id)) {
          map.set(emp.id, {
            id: emp.id,
            nome_fantasia: emp.nome_fantasia,
          });
        }
      }
    }

    return Array.from(map.values()).sort((a, b) =>
      a.nome_fantasia.localeCompare(b.nome_fantasia, "pt-BR")
    );
  }, [coordOptions]);

  const empresaDropdown = useMemo(
    () => empresaOptions.map((e) => e.nome_fantasia),
    [empresaOptions]
  );

  const handleEmpresaChange = (value: string) => {
    const empresaNome = String(value || "")
      .trim()
      .toUpperCase();
    setNomeEmpresa(empresaNome);

    const empresa = empresaOptions.find((e) => e.nome_fantasia === empresaNome);

    if (!empresa) {
      setEmpresaClienteId(null);
      setCoordenadoresResponsaveisIds([]);
      setCoordenadoresResponsaveisNomes([]);
      return;
    }

    setEmpresaClienteId(empresa.id);

    const coordsDaEmpresa = coordOptions.filter((coord) =>
      (coord.empresas_responsavel || []).some((emp) => emp.id === empresa.id)
    );

    const ids = coordsDaEmpresa.map((c) => c.id);
    const nomes = coordsDaEmpresa.map((c) => c.nome);

    setCoordenadoresResponsaveisIds(ids);
    setCoordenadoresResponsaveisNomes(nomes);
  };

  const handleToggleCoord = (coord: CoordenadorOption) => {
    const exists = coordenadoresResponsaveisIds.includes(coord.id);

    if (exists) {
      setCoordenadoresResponsaveisIds(
        coordenadoresResponsaveisIds.filter((id) => id !== coord.id)
      );
      setCoordenadoresResponsaveisNomes(
        coordenadoresResponsaveisNomes.filter((n) => n !== coord.nome)
      );
      return;
    }

    setCoordenadoresResponsaveisIds([
      ...coordenadoresResponsaveisIds,
      coord.id,
    ]);
    setCoordenadoresResponsaveisNomes([
      ...coordenadoresResponsaveisNomes,
      coord.nome,
    ]);
  };

  return (
    <div>
      <TextFieldRow
        center={{
          label: "Destinatário da Coleta",
          value: responsavelColeta,
          onChange: toUpper(setResponsavelColeta),
          placeholder: loadingColabs
            ? "CARREGANDO COLABORADORES..."
            : erroColabs
            ? "FALHA AO CARREGAR. DIGITE MANUALMENTE."
            : "SELECIONE O DESTINATÁRIO DA COLETA",
          options: colabDropdown,
        }}
      />

      <TextFieldRow
        left={{
          label: "Empresa",
          value: nomeEmpresa,
          onChange: (v: string) => handleEmpresaChange(v),
          placeholder: loadingCoords
            ? "CARREGANDO EMPRESAS..."
            : erroCoords
            ? "FALHA AO CARREGAR. DIGITE MANUALMENTE."
            : "SELECIONE A EMPRESA",
          options: empresaDropdown,
        }}
        right={{
          label: "Embarcação",
          value: nomeEmbarcacao,
          onChange: toUpper(setNomeEmbarcacao),
          placeholder: "Digite o nome da Embarcação",
        }}
      />

      <TextField
        label="Número da O.S"
        value={numeroOS}
        onChange={toUpper(setNumeroOS)}
        placeholder="Digite aqui o número da O.S"
        uppercase={true}
      />

      <div style={{ marginTop: 14 }}>
        <div
          style={{
            fontSize: 12,
            fontWeight: 600,
            marginBottom: 8,
            color: "#111827",
          }}
        >
          Coordenador responsável
        </div>

        <div
          style={{
            border: "1px solid #D1D5DB",
            borderRadius: 6,
            padding: 10,
            minHeight: 44,
            background: "#fff",
          }}
        >
          {loadingCoords && (
            <div style={{ fontSize: 13, color: "#6B7280" }}>
              CARREGANDO COORDENADORES...
            </div>
          )}

          {!loadingCoords && erroCoords && (
            <div style={{ fontSize: 13, color: "#B91C1C" }}>
              FALHA AO CARREGAR COORDENADORES.
            </div>
          )}

          {!loadingCoords && !erroCoords && coordOptions.length === 0 && (
            <div style={{ fontSize: 13, color: "#6B7280" }}>
              NENHUM COORDENADOR DISPONÍVEL.
            </div>
          )}

          {!loadingCoords && !erroCoords && coordOptions.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {coordOptions.map((coord) => {
                const checked = coordenadoresResponsaveisIds.includes(coord.id);
                return (
                  <label
                    key={coord.id}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      border: "1px solid #D1D5DB",
                      borderRadius: 16,
                      padding: "6px 10px",
                      cursor: "pointer",
                      background: checked ? "#EEF6FF" : "#fff",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => handleToggleCoord(coord)}
                    />
                    <span style={{ fontSize: 13 }}>{coord.nome}</span>
                  </label>
                );
              })}
            </div>
          )}
        </div>

        <div style={{ marginTop: 8, fontSize: 12, color: "#4B5563" }}>
          Selecionados:{" "}
          {coordenadoresResponsaveisNomes.length > 0
            ? coordenadoresResponsaveisNomes.join(", ")
            : "nenhum"}
        </div>
      </div>
    </div>
  );
};

export default ServicoMovement;
